import { useTranslation } from "react-i18next";
import { Grid, GridColumn, GridRow, Icon, Popup } from "semantic-ui-react";

import { useCan } from "components";
import { STATUS_IDENTIFIERS } from "models";
import {
  getFullTyreDescription,
  getTyreSize
} from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/utils";
import { useTyreReplacementUpdateMutation } from "modules/AppointmentDetails/components/VehicleTyres/hooks";
import { useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { getDisplayedPrice, getTyreSeasonIcon } from "utils/common";
import { formatPrice } from "utils/formatPrice";

import type { VehicleTyreReplacement } from "models";

type CustomerCommunicationReplacementTyreProps = {
  replacement: VehicleTyreReplacement;
  isLast: boolean;
  showVat: boolean;
  appointment_status_identifier: STATUS_IDENTIFIERS;
};

export const CustomerCommunicationReplacementTyre = ({ replacement, appointment_status_identifier, isLast, showVat }: CustomerCommunicationReplacementTyreProps) => {
  const { t } = useTranslation();

  const tyreSeasonIcon = getTyreSeasonIcon(replacement.tyre?.season);
  const appointmentLocation = useAppointmentLocation();
  const canUpdateReplacements = useCan("update", "vehicle-tyre-replacements");
  const updateReplacements = useTyreReplacementUpdateMutation();

  const toggleVisible = (replacement: VehicleTyreReplacement) => {
    if (!canUpdateReplacements || appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus) return;

    updateReplacements.mutate({
      ...replacement,
      visible_to_customer: !replacement.visible_to_customer
    });
  };

  const tyreDescription = getFullTyreDescription(replacement.tyre, appointmentLocation?.is_tyre_net_enabled);
  const displayedPrice = getDisplayedPrice(replacement.price, appointmentLocation, showVat);

  return (
    <Grid>
      <GridRow className={`CustomerCommunicationTyreTableItem__row ${isLast ? "is-last" : ""}`}>
        <GridColumn width={2}>
          <p className="replace-with">
            <strong>{t("v8_replace_with", "Replace with")}:</strong>
          </p>
        </GridColumn>

        <GridColumn width={2}>
          <Popup
            hoverable
            content={replacement.tyre ? getTyreSize(replacement.tyre) : ""}
            trigger={
              <div className="tyre-season-container">
                {tyreSeasonIcon && <Icon className={tyreSeasonIcon} />}
                <p>{replacement.tyre ? getTyreSize(replacement.tyre) : ""}</p>
              </div>
            }
          />
        </GridColumn>
        <GridColumn width={3}>
          <Popup hoverable content={tyreDescription} trigger={<p className="-truncated-text">{tyreDescription}</p>} />
        </GridColumn>
        <GridColumn width={2}>
          <Popup hoverable content={replacement.tyre?.manufacturer} trigger={<p className="-truncated-text">{replacement.tyre?.manufacturer}</p>} />
        </GridColumn>
        <GridColumn width={1} />
        <GridColumn width={3}>
          <Popup content={replacement.remark} trigger={<p className="-truncated-text">{replacement.remark}</p>} disabled={!replacement.remark} />
        </GridColumn>
        <GridColumn width={1}>{replacement.price ? "€ " + formatPrice(displayedPrice) : ""}</GridColumn>
        <GridColumn width={2} textAlign="right">
          <div className="icons-container">
            <Icon
              className={`eye ${replacement.visible_to_customer ? "green" : "slash red"} ${canUpdateReplacements ? "pointer" : ""}`}
              size="small"
              onClick={() => toggleVisible(replacement)}
            />
          </div>
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
